import type { Document, DocumentType, Job } from '../../../core/types/data-models';

/** Human-readable job type, for the In Progress chips and the failure / busy notices. */
export const JOB_TYPE_LABEL: Record<string, string> = {
  GENERATE_DOCUMENT: 'Generate Document',
  GENERATE_DOCUMENT_QUESTIONS: 'Prepare Questions',
  REGENERATE_DOCUMENT_SECTION: 'Regenerate Section',
  GENERATE_DOCUMENT_SECTIONS: 'Generate Sections',
  GENERATE_DOCUMENT_METADATA: 'Generate Dashboard',
  SCORE_DOCUMENT_SECTIONS: 'Score Sections',
  EXTRACT_DOCUMENT_ENTITIES: 'Extract Entities',
  GENERATE_VALIDATION_SUMMARY: 'Generate Validation Summary',
  ANALYZE_PROJECT_SOURCES: 'Analyze Company Sources',
  EXAMPLE: 'Example Job',
};

// Minute-level granularity (unlike formatAge) so the "started" line is
// still meaningful in the 1-60 minute range this is actually used for.
export function formatStarted(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime();
  const totalMin = Math.floor(ms / 60_000);
  if (totalMin < 1) return 'just now';
  if (totalMin < 60) return `${totalMin}m ago`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m > 0 ? `${h}h ${m}m ago` : `${h}h ago`;
}

// job.progressAdditionalData is JSON-encoded and shape depends on jobType —
// currently only the LLM-generation steps of GENERATE_DOCUMENT and
// GENERATE_DOCUMENT_METADATA populate `{ outputChars, outputTokens }` (from
// the LLM service's onStreamProgress callback), so a job stuck at a fixed
// percent while the model streams a long response still shows a live,
// climbing number instead of looking frozen.
export function formatGenerationProgress(additionalData?: string | null): string | null {
  if (!additionalData) return null;
  let parsed: { outputChars?: number; outputTokens?: number } | null = null;
  try {
    parsed = JSON.parse(additionalData);
  } catch {
    return null;
  }
  if (parsed?.outputTokens) return `~${parsed.outputTokens.toLocaleString()} tokens generated so far`;
  if (parsed?.outputChars) return `${parsed.outputChars.toLocaleString()} characters generated so far`;
  return null;
}

// Short form of formatGenerationProgress for the JobChip's always-visible
// label (as opposed to its hover tooltip), where space is tight — e.g.
// "4.2k tok" instead of "~4,200 tokens generated so far".
export function formatGenerationProgressCompact(additionalData?: string | null): string | null {
  if (!additionalData) return null;
  let parsed: { outputChars?: number; outputTokens?: number } | null = null;
  try {
    parsed = JSON.parse(additionalData);
  } catch {
    return null;
  }
  const count = parsed?.outputTokens ?? parsed?.outputChars;
  if (!count) return null;
  const unit = parsed?.outputTokens ? 'tok' : 'chars';
  const value = count >= 1000 ? `${(count / 1000).toFixed(1)}k` : `${count}`;
  return `${value} ${unit}`;
}

// Resolves the human-readable document type name a job is working on, so the
// "In Progress" chip can show e.g. "Executive Summary" instead of just the
// generic job-type label. Two shapes of job.data carry this depending on job
// type: GENERATE_DOCUMENT/-_QUESTIONS embed `documentType` directly (the
// document doesn't exist yet at job-creation time); every other
// document-scoped job type carries `documentId` instead, resolved through
// `docById` (loaded from this project's documents list).
export function getJobDocTypeName(
  job: Job,
  docById: Map<number, Document>,
  docTypeMap: Map<string, DocumentType>,
): string | null {
  if (!job.data) return null;
  let parsed: { documentType?: string; documentId?: number } | null = null;
  try {
    parsed = JSON.parse(job.data);
  } catch {
    return null;
  }
  const documentType =
    parsed?.documentType ?? (parsed?.documentId != null ? docById.get(parsed.documentId)?.documentType : undefined);
  return documentType ? (docTypeMap.get(documentType)?.name ?? null) : null;
}
